import animejs from 'animejs/lib/anime.min.js';
import DOMElement from "../../../../../components/base/dom-element";
import LocalizationsHelper from "../../../../../lib/localizations-helper";
import Api from "../../../../../lib/api";
import EventEmitter from "../../../../../components/base/form-v2/event-emitter";

const GOOGLE_PAY_BASE_REQUEST = {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
        {
            type: 'CARD',
            parameters: {
                allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                allowedCardNetworks: ['VISA', 'MASTERCARD'],
            },
        },
    ],
};

export default class AgPayButtonService {
    constructor(props = {}) {
        this.$root = props.$root;

        if (!this.$root) {
            return;
        }

        this.$buttons = this.$root.querySelectorAll('.ag-pay-button');

        this.onPaymentTokenReceived = props.onPaymentTokenReceived;
        this.onPaymentError = props.onPaymentError;
        this.onPaymentCancel = props.onPaymentCancel;

        if (!this.$buttons.length) {
            return;
        }

        this.aPayService = new ApplePayService();
        this.gPayService = new GooglePayService();
        this.eventEmitter = new EventEmitter(props.subscriptions || []);

        this.payServiceName = null;
        this.paymentInfo = null;

        this.pay = this._pay.bind(this);

        this.PAY_BUTTON_CLICKED_EVENT = 'payButtonClicked';

        this._init();
    }

    static getButtonElement() {
        return new DOMElement('button', {
            class: 'button -xl -p -full-size ag-pay-button -loading',
            attributes: {
                type: 'button',
                disabled: true,
                'data-form': 'button',
                'data-validate': 'true',
            },
            html: `${LocalizationsHelper.get('component.checkout.payWith')} <svg class="ag-pay-button-spinner" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity=".1" cx="16" cy="16" r="14" stroke="#4ED401" stroke-width="3"/><path d="M30 16c0-7.732-6.268-14-14-14S2 8.268 2 16" stroke="#4ED401" stroke-width="3"/></svg>`
        });
    }

    /**
     * @param {PaymentInfoModel} paymentInfo
     */
    updatePaymentInfo(paymentInfo) {
        this.paymentInfo = paymentInfo;

        if (
            !this.paymentInfo ||
            this.paymentInfo.paymentSystem !== 'liqpay' ||
            (this.paymentInfo.paymentFormat !== 'full' &&
                this.paymentInfo.paymentFormat !== 'partial')
        ) {
            this.hide(true);
            return;
        }

        this.show(true);
    }

    hide(animate = false) {
        this.$buttons.forEach(($button) => {
            if ($button) {
                if (!animate) {
                    $button.classList.add('-hidden');
                } else {
                    this._toggleAnimated($button, 'hide');
                }
            }
        });
    }

    show(animate = false) {
        this.$buttons.forEach(($button) => {
            if ($button) {
                if (!animate) {
                    $button.classList.remove('-hidden');
                } else {
                    this._toggleAnimated($button, 'show');
                }
            }
        });
    }

    setupPayButtons(loading = false, refreshListeners = false) {
        if (refreshListeners) {
            this.$buttons = this.$root.querySelectorAll('.ag-pay-button');
        }

        this.$buttons.forEach(($button) => {
            this._setupPayButton($button, loading, refreshListeners);
        });
    }

    _init() {
        Promise.all([
            this.aPayService.loadScript(),
            this.gPayService.loadScript(),
        ]).then(([aPayCanMakePayments, gPayCanMakePayments]) => {
            this.$buttons.forEach(($button) => {
                if (aPayCanMakePayments) {
                    this.payServiceName = 'apple';
                } else if (gPayCanMakePayments) {
                    this.payServiceName = 'google';
                }

                this._setupPayButton($button, false, true);
            });
        });
    }

    _setupPayButton($button, loading = false, refreshListeners = false) {
        if (!$button) {
            return;
        }

        if (loading) {
            $button.classList.add('-loading');
            setTimeout(() => {
                $button.innerHTML  = `${LocalizationsHelper.get('component.checkout.payWith')} <svg class="ag-pay-button-spinner" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity=".1" cx="16" cy="16" r="14" stroke="#4ED401" stroke-width="3"/><path d="M30 16c0-7.732-6.268-14-14-14S2 8.268 2 16" stroke="#4ED401" stroke-width="3"/></svg>`;
            }, 5);
        } else if (this.payServiceName === 'apple') {
            $button.classList.add('-apple');
            $button.innerHTML  = `${LocalizationsHelper.get('component.checkout.payWith')} <svg class="ag-pay-button-icon" width="40" height="16" viewBox="0 0 40 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.12506 2.0628C6.66835 2.60324 5.93762 3.0295 5.20689 2.9686C5.11555 2.23787 5.4733 1.46147 5.89195 0.981922C6.34866 0.426261 7.1479 0.0304472 7.7949 0C7.87102 0.76118 7.57416 1.50714 7.12506 2.0628ZM7.78729 3.11323C6.72925 3.05233 5.82344 3.71456 5.32106 3.71456C4.81107 3.71456 4.04228 3.14367 3.20498 3.1589C2.1165 3.17412 1.10413 3.79068 0.548468 4.7726C-0.593302 6.73644 0.251608 9.64415 1.35532 11.2426C1.89576 12.0343 2.54276 12.902 3.39528 12.8716C4.20213 12.8411 4.52183 12.3463 5.49614 12.3463C6.47806 12.3463 6.75969 12.8716 7.61222 12.8563C8.49518 12.8411 9.05085 12.0647 9.59128 11.2731C10.2078 10.3749 10.459 9.49952 10.4743 9.45385C10.459 9.43863 8.76921 8.79163 8.75399 6.84301C8.73876 5.21408 10.0861 4.43768 10.1469 4.39201C9.38577 3.26546 8.19832 3.14367 7.78729 3.11323ZM13.8996 0.905804V12.7726H15.7416V8.71551H18.2916C20.6208 8.71551 22.2573 7.11703 22.2573 4.80304C22.2573 2.48906 20.6512 0.905804 18.3525 0.905804H13.8996ZM15.7416 2.45861H17.8653C19.4638 2.45861 20.3772 3.31113 20.3772 4.81066C20.3772 6.31018 19.4638 7.17031 17.8577 7.17031H15.7416V2.45861ZM25.6217 12.8639C26.7787 12.8639 27.852 12.2778 28.3391 11.3492H28.3772V12.7726H30.0822V6.86584C30.0822 5.15319 28.7121 4.04948 26.6037 4.04948C24.6474 4.04948 23.2012 5.16841 23.1479 6.70599H24.8073C24.9443 5.97526 25.6217 5.49572 26.5504 5.49572C27.6769 5.49572 28.3087 6.02093 28.3087 6.98763V7.64225L26.0099 7.77926C23.871 7.90866 22.714 8.78401 22.714 10.3064C22.714 11.844 23.9091 12.8639 25.6217 12.8639ZM26.1165 11.4558C25.1346 11.4558 24.5104 10.9838 24.5104 10.2607C24.5104 9.51475 25.1117 9.08088 26.2611 9.01237L28.3087 8.88297V9.55281C28.3087 10.6641 27.3648 11.4558 26.1165 11.4558ZM32.3582 16C34.1546 16 34.9995 15.3149 35.7378 13.2369L38.9728 4.16365H37.1003L34.931 11.1741H34.8929L32.7235 4.16365H30.7978L33.9186 12.803L33.7511 13.3283C33.4695 14.2188 33.0128 14.5614 32.1983 14.5614C32.0537 14.5614 31.7721 14.5461 31.6579 14.5309V15.9543C31.7645 15.9848 32.2212 16 32.3582 16Z" fill="white"/></svg>`;
            $button.classList.remove('-loading');
        } else if (this.payServiceName === 'google') {
            $button.classList.add('-google');
            $button.innerHTML  = `${LocalizationsHelper.get('component.checkout.payWith')} <svg class="ag-pay-button-icon" width="51" height="20" viewBox="0 0 41 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.3893 7.81651V12.5066H17.9014V0.924118H21.8475C22.3186 0.914279 22.7869 0.99782 23.2255 1.16992C23.664 1.34202 24.0642 1.59927 24.4028 1.92683C24.7449 2.2345 25.0171 2.61183 25.2012 3.03345C25.3853 3.45507 25.4771 3.91122 25.4702 4.37124C25.48 4.83372 25.3897 5.29284 25.2055 5.71715C25.0212 6.14146 24.7474 6.52091 24.4028 6.82951C23.7128 7.48751 22.861 7.8162 21.8475 7.81559H19.3893V7.81651ZM19.3893 2.3501V6.3933H21.8845C22.1581 6.40141 22.4303 6.35234 22.6838 6.24922C22.9373 6.1461 23.1665 5.99121 23.3567 5.79445C23.5459 5.61048 23.6963 5.39044 23.799 5.14734C23.9017 4.90423 23.9547 4.643 23.9547 4.37909C23.9547 4.11518 23.9017 3.85395 23.799 3.61085C23.6963 3.36775 23.5459 3.14771 23.3567 2.96374C23.1689 2.76284 22.9405 2.60415 22.6867 2.4982C22.4329 2.39224 22.1594 2.34145 21.8845 2.34917H19.3893V2.3501Z" fill="white"/>
                <path d="M28.8989 4.32324C29.9986 4.32324 30.8667 4.61713 31.5032 5.20489C32.1396 5.79266 32.4575 6.59853 32.4569 7.6225V12.5067H31.0337V11.4069H30.969C30.3529 12.3126 29.5335 12.7655 28.5107 12.7655C27.6377 12.7655 26.9073 12.5067 26.3196 11.9892C26.0375 11.7516 25.812 11.4541 25.6594 11.1184C25.5069 10.7827 25.4312 10.4171 25.4379 10.0484C25.4379 9.22838 25.7478 8.57623 26.3676 8.09197C26.9874 7.60771 27.8148 7.36496 28.8499 7.36373C29.7334 7.36373 30.461 7.52546 31.0328 7.84892V7.50883C31.0345 7.25747 30.9802 7.00889 30.8739 6.78111C30.7676 6.55333 30.6119 6.35206 30.4182 6.1919C30.0247 5.83691 29.512 5.64321 28.9821 5.64941C28.1509 5.64941 27.4932 5.99998 27.009 6.70111L25.6985 5.87583C26.4194 4.84077 27.4862 4.32324 28.8989 4.32324ZM26.9739 10.0808C26.9729 10.2701 27.0171 10.457 27.103 10.6258C27.1888 10.7946 27.3137 10.9404 27.4674 11.0511C27.7964 11.31 28.2049 11.4471 28.6235 11.4393C29.2514 11.4382 29.8532 11.1883 30.2971 10.7443C30.79 10.2804 31.0365 9.73605 31.0365 9.11132C30.5725 8.74166 29.9256 8.55682 29.0957 8.55682C28.4913 8.55682 27.9874 8.70253 27.5838 8.99395C27.1763 9.28968 26.9739 9.64918 26.9739 10.0808Z" fill="white"/>
                <path d="M40.6267 4.58203L35.6584 16H34.1224L35.9661 12.0049L32.6992 4.58203H34.3165L36.6777 10.2749H36.7101L39.0066 4.58203H40.6267Z" fill="white"/>
                <path d="M13.3769 6.80561C13.3775 6.35217 13.3392 5.89952 13.2624 5.45264H6.9873V8.01534H10.5814C10.5078 8.42464 10.3522 8.81481 10.1238 9.16232C9.89536 9.50984 9.59896 9.80752 9.25242 10.0374V11.7009H11.3974C12.6533 10.5429 13.3769 8.83045 13.3769 6.80561Z" fill="#4285F4"/>
                <path d="M6.98712 13.3077C8.78276 13.3077 10.2947 12.7181 11.3972 11.7015L9.25224 10.038C8.65523 10.4428 7.88633 10.6739 6.98712 10.6739C5.25155 10.6739 3.77843 9.50386 3.25166 7.92725H1.04199V9.64156C1.59581 10.7436 2.44503 11.67 3.49485 12.3174C4.54466 12.9648 5.75375 13.3077 6.98712 13.3077Z" fill="#34A853"/>
                <path d="M3.25121 7.92732C2.97272 7.10116 2.97272 6.20649 3.25121 5.38033V3.66602H1.04154C0.575644 4.59309 0.333008 5.61627 0.333008 6.65383C0.333008 7.69138 0.575644 8.71457 1.04154 9.64164L3.25121 7.92732Z" fill="#FBBC04"/>
                <path d="M6.98712 2.63381C7.93604 2.6183 8.85294 2.97683 9.53965 3.6319L11.4388 1.73275C10.2345 0.601647 8.63917 -0.0193473 6.98712 -5.17195e-05C5.75375 4.4927e-06 4.54466 0.342862 3.49485 0.990244C2.44503 1.63763 1.59581 2.56405 1.04199 3.66609L3.25166 5.38041C3.77843 3.80379 5.25155 2.63381 6.98712 2.63381Z" fill="#EA4335"/>
            </svg>`;
            $button.classList.remove('-loading');
        } else {
            $button.remove();
            return;
        }

        if (refreshListeners) {
            $button.removeEventListener('click', this.pay);
            $button.addEventListener('click', this.pay);
        }
    }

    _pay(e) {
        if (!this.payServiceName || !this.paymentInfo) {
            return;
        }

        this.eventEmitter.dispatch(this.PAY_BUTTON_CLICKED_EVENT, e);

        if (this.payServiceName === 'apple') {
            this.aPayService.pay(
                {
                    label: craft.page.courseTitle,
                    type: 'final',
                    amount: this.paymentInfo.finalCost,
                },
                (token) => this.onPaymentTokenReceived(token, 'apple'),
                this.onPaymentCancel,
                this.onPaymentError
            );
        } else if (this.payServiceName === 'google') {
            this.gPayService.pay(
                    {
                    label: craft.page.courseTitle,
                    gateway: this.paymentInfo.paymentSystem,
                    gatewayMerchantId: this.paymentInfo.gatewayId,
                    totalPrice: this.paymentInfo.finalCost,
                },
                (token) => this.onPaymentTokenReceived(token, 'google'),
                this.onPaymentError
            );
        }
    }

    _toggleAnimated($button, action = 'hide') {
        if (
            action === 'show' && !$button.classList.contains('-hidden') ||
            action === 'hide' && $button.classList.contains('-hidden')
        ) {
            return;
        }

        const $parent = $button.closest('.f-footer-buttons');
        const parentHeight = $parent.offsetHeight;

        if (!parentHeight) {
            if (action === 'show') {
                $button.classList.remove('-hidden');
            } else {
                $button.classList.add('-hidden');
            }
            return;
        }

        $parent.style.height = `${parentHeight}px`;

        if (action === 'show') {
            animejs({
                targets: $parent,
                height: `${parentHeight + 46}px`,
                duration: 150,
                easing: 'cubicBezier(0, 0.55, 0.45, 1)',
                complete: (anim) => {
                    if (anim.completed) {
                        $button.classList.remove('-hidden');
                        $parent.removeAttribute('style');
                    }
                }
            });
        } else {
            $button.classList.add('-hidden');
            animejs({
                targets: $parent,
                height: `${parentHeight - 46}px`,
                duration: 150,
                easing: 'cubicBezier(0, 0.55, 0.45, 1)',
                complete: (anim) => {
                    if (anim.completed) {
                        $parent.removeAttribute('style');
                    }
                }
            });
        }
    }
}

class ApplePayService {
    constructor() {
        this.scriptSrc =
            'https://applepay.cdn-apple.com/jsapi/v1.1.0/apple-pay-sdk.js';
        this.scriptLoaded = false;
        this.ApplePaySession = null;
    }

    _validateMerchant() {
        return Api.get(`${Api.lmsApiUrl}/online-payment/site/apple/validate-merchant`);
    }

    loadScript() {
        return new Promise((resolve) => {
            // resolve if already loaded
            if (this.scriptLoaded) {
                this.canMakePayments()
                    .then((canMakePayments) => {
                        resolve(canMakePayments);
                    })
                    .catch((error) => {
                        resolve(false);
                    });
            } else {
                // load script
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = this.scriptSrc;
                script.onload = () => {
                    this.scriptLoaded = true;
                    this.ApplePaySession = window.ApplePaySession;

                    if (!this.ApplePaySession) {
                        resolve(false);
                        return;
                    }

                    this.canMakePayments()
                        .then((canMakePayments) => {
                            resolve(canMakePayments);
                        })
                        .catch((error) => {
                            console.error(error);
                            resolve(false);
                        });
                };
                script.onerror = (error) => resolve(false);
                document
                    .getElementsByTagName('head')[0]
                    .appendChild(script);
            }
        });
    }

    canMakePayments() {
        if (!this.scriptLoaded || !this.ApplePaySession) {
            return new Promise((resolve) => resolve(false));
        }

        try {
            return new Promise((resolve) => {
                const result = this.ApplePaySession.canMakePayments();
                resolve(result);
            });
        } catch (error) {
            return new Promise((resolve) => resolve(false));
        }
    }

    pay(payItem, onPaymentTokenReceived, onCancel, onError) {
        if (!window.ApplePaySession) {
            return;
        }

        const ApplePaySession = window.ApplePaySession;

        // Define ApplePayPaymentRequest
        // @doc https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest
        const request = {
            countryCode: 'UA',
            currencyCode: 'UAH',
            merchantCapabilities: ['supports3DS'],
            supportedNetworks: ['visa', 'masterCard'],
            total: payItem,
        };

        // Create ApplePaySession
        // @doc https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/2320659-applepaysession
        const session = new ApplePaySession(3, request);

        session.onvalidatemerchant = (event) => {
            this._validateMerchant().then(({ success, response }) => {
                    if (success && response) {
                        session.completeMerchantValidation(response);
                        return;
                    }

                    session.completePayment({
                        status: ApplePaySession.STATUS_FAILURE,
                    });
                if (onError) {
                    onError();
                }
                }).catch((error) => {
                    session.completePayment({
                        status: ApplePaySession.STATUS_FAILURE,
                    });
                if (onError) {
                    onError();
                }
                });
        };

        session.onpaymentauthorized = (event) => {
            if (!onPaymentTokenReceived) {
                session.completePayment({
                    status: ApplePaySession.STATUS_FAILURE,
                });
                if (onError) {
                    onError();
                }
                return;
            }

            const ap_token_object = event['payment']['token'];
            const ap_token = btoa(JSON.stringify(ap_token_object));

            onPaymentTokenReceived(ap_token)
                .then(() => {
                    session.completePayment({
                        status: ApplePaySession.STATUS_SUCCESS,
                    });
                })
                .catch(() => {
                    session.completePayment({
                        status: ApplePaySession.STATUS_FAILURE,
                    });
                    if (onError) {
                        onError();
                    }
                });
        };

        session.oncancel = (event) => {
            if (!onCancel) return;
            onCancel();
        };

        session.begin();
    }
}

class GooglePayService {
    constructor() {
        this.scriptSrc = 'https://pay.google.com/gp/p/js/pay.js';
        this.scriptLoaded = false;
        this.paymentsClient = null;
    }

    loadScript() {
        return new Promise((resolve) => {
            // resolve if already loaded
            if (this.scriptLoaded) {
                this.canMakePayments()
                    .then((canMakePayments) => {
                        resolve(canMakePayments);
                    })
                    .catch((error) => {
                        resolve(false);
                    });
            } else {
                // load script
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = this.scriptSrc;
                script.onload = () => {
                    this.scriptLoaded = true;

                    if (!window.google) {
                        resolve(false);
                        return;
                    }

                    this.paymentsClient = new window.google.payments.api.PaymentsClient({
                        environment: craft.env.devMode ? 'TEST' : 'PRODUCTION',
                    });

                    this.canMakePayments()
                        .then((canMakePayments) => {
                            resolve(canMakePayments);
                        })
                        .catch((error) => {
                            resolve(false);
                        });
                };
                script.onerror = (error) => resolve(false);
                document
                    .getElementsByTagName('head')[0]
                    .appendChild(script);
            }
        });
    }

    canMakePayments() {
        if (!this.scriptLoaded || !this.paymentsClient) {
            return new Promise((resolve) => resolve(false));
        }

        return new Promise((resolve) => {
            this.paymentsClient
                .isReadyToPay(GOOGLE_PAY_BASE_REQUEST)
                .then((response) => {
                    resolve(response.result);
                })
                .catch((error) => {
                    resolve(false);
                });
        });
    }

    pay(paymentData, onPaymentTokenReceived, onError) {
        const paymentDataRequest = Object.assign({}, GOOGLE_PAY_BASE_REQUEST, {
            merchantInfo: {
                merchantId: 'BCR2DN4T4GZYDEY2',
                merchantName: 'Hillel IT School',
            },
            allowedPaymentMethods: [
                {
                    type: 'CARD',
                    parameters: {
                        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                        allowedCardNetworks: ['VISA', 'MASTERCARD'],
                    },
                    tokenizationSpecification: {
                        type: 'PAYMENT_GATEWAY',
                        parameters: {
                            gateway: paymentData.gateway,
                            gatewayMerchantId: paymentData.gatewayMerchantId,
                        },
                    },
                },
            ],
            transactionInfo: {
                totalPrice: `${paymentData.totalPrice}`,
                totalPriceStatus: 'FINAL',
                currencyCode: 'UAH',
            },
        });

        this.paymentsClient
            .loadPaymentData(paymentDataRequest)
            .then((gpData) => {
                const gpayTokenObject =
                    gpData.paymentMethodData.tokenizationData.token;
                const gpayToken = btoa(gpayTokenObject);
                return onPaymentTokenReceived(gpayToken);
            })
            .catch((err) => {
                console.error(err);
                if (onError) {
                    onError();
                }
            });
    }
}